import axios from 'axios';
import { refreshAccessTokenOnce } from './tokenRefresh';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const ACCESS_TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refresh_token';

const api = axios.create({
  baseURL: API_URL,
  timeout: 20000, // 20s - prevents PWA Builder/crawlers from hanging on slow/unreachable backend
  headers: {
    'Content-Type': 'application/json',
  },
});

const getAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY);
const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);

export const setAccessToken = (token) => {
  if (token) {
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
    return;
  }
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  delete api.defaults.headers.common.Authorization;
};

export const setRefreshToken = (token) => {
  if (token) {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
    return;
  }
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

export const clearAuthTokens = () => {
  setAccessToken(null);
  setRefreshToken(null);
};

const isAuthPath = (url = '') => {
  const cleanUrl = String(url || '');
  return (
    cleanUrl.includes('/api/auth/login') ||
    cleanUrl.includes('/api/auth/signup') ||
    cleanUrl.includes('/api/auth/firebase/exchange') ||
    cleanUrl.includes('/api/auth/firebase/complete-profile') ||
    cleanUrl.includes('/api/auth/refresh')
  );
};

// Set auth token if available on app bootstrap
const bootToken = getAccessToken();
if (bootToken) {
  api.defaults.headers.common.Authorization = `Bearer ${bootToken}`;
}

// Add request interceptor to always get fresh token from localStorage
api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const runRefreshFlow = async () => {
  // Shared with earlyFetch so concurrent 401s do not rotate the refresh token twice.
  const newAccessToken = await refreshAccessTokenOnce();
  // Keep axios defaults in sync with localStorage (refreshAccessTokenOnce already wrote storage).
  setAccessToken(newAccessToken);
  return newAccessToken;
};

// Add response interceptor for error handling and logging
api.interceptors.response.use(
  (response) => {
    // Return successful responses as-is
    return response;
  },
  async (error) => {
    // Log detailed error information for debugging
    if (error.response) {
      // Server responded with error status
      console.error('API Error:', {
        url: error.config?.url,
        method: error.config?.method?.toUpperCase(),
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        message: error.response.data?.detail || error.message,
      });
    } else if (error.request) {
      // Request was made but no response received
      console.error('Network Error:', {
        url: error.config?.url,
        method: error.config?.method?.toUpperCase(),
        message: 'No response from server. Check network connection and API URL.',
      });
    } else {
      // Error in request setup
      console.error('Request Error:', error.message);
    }

    const originalRequest = error?.config || {};
    const statusCode = error?.response?.status;
    const shouldSkipRefresh = originalRequest?.headers?.['X-Skip-Auth-Refresh'] === 'true';
    const canRetry =
      statusCode === 401 &&
      !originalRequest._retry &&
      !shouldSkipRefresh &&
      !isAuthPath(originalRequest.url);

    if (canRetry) {
      originalRequest._retry = true;
      try {
        const newAccessToken = await runRefreshFlow();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api.request(originalRequest);
      } catch (refreshError) {
        clearAuthTokens();
        return Promise.reject(refreshError);
      }
    }

    // Return error to be handled by calling code
    return Promise.reject(error);
  }
);

// Firebase Auth Exchange
export const exchangeFirebaseToken = (idToken) =>
  api.post('/api/auth/firebase/exchange', { id_token: idToken });
export const completeFirebaseProfile = (payload) =>
  api.post('/api/auth/firebase/complete-profile', payload);
export const refreshSession = (refreshToken) =>
  api.post(
    '/api/auth/refresh',
    { refresh_token: refreshToken },
    { headers: { 'X-Skip-Auth-Refresh': 'true' } }
  );
export const logoutSession = (refreshToken) =>
  api.post('/api/auth/logout', { refresh_token: refreshToken || '' });

// User Profile
export const updateUserProfile = (data) => api.put('/api/auth/profile', data);
export const updateUserEmail = (email) => api.put('/api/auth/profile', { email });
export const deleteMyAccount = () => api.delete('/api/auth/me');

// Activities
export const getActivities = () => api.get('/api/activities');
export const getActivity = (id) => api.get(`/api/activities/${id}`);
export const createActivity = (data) => api.post('/api/activities/', data);
export const parseActivityIntent = (message, groups) =>
  api.post('/api/activities/parse-intent', { message, groups });
export const updateActivity = (id, data) => api.put(`/api/activities/${id}`, data);
export const deleteActivity = (id) => api.delete(`/api/activities/${id}`);
export const exitActivity = (id) => api.post(`/api/activities/${id}/exit`);
export const expressInterest = (activityId, data) => 
  api.post(`/api/activities/${activityId}/interest`, data);
export const getInterestStatus = (activityId) => 
  api.get(`/api/activities/${activityId}/interest/status`);
export const redeemRegistration = (activityId) =>
  api.post(`/api/activities/${activityId}/interest/redeem`);
export const getActivityEOI = (activityId) =>
  api.get(`/api/activities/${activityId}/interest`);
export const joinActivity = (activityId) => 
  api.post(`/api/activities/${activityId}/join`);
export const getActivityPaymentStatus = (activityId) =>
  api.get(`/api/payments/activity/${activityId}/status`);
export const getActivityTickets = (activityId) =>
  api.get(`/api/activities/${activityId}/tickets`);
export const saveActivityTickets = (activityId, data) =>
  api.put(`/api/activities/${activityId}/tickets`, data);

// Groups
export const getGroups = (skip = 0, limit = 100) => api.get(`/api/groups?skip=${skip}&limit=${limit}`);
export const getMyGroups = () => api.get('/api/groups/my-groups');
export const getExitedGroups = () => api.get('/api/groups/exited-groups');
export const getPendingInvites = () => api.get('/api/groups/pending-invites');
export const joinGroup = (groupId) => api.post(`/api/groups/${groupId}/join`);
export const getGroupInvitePreview = (inviteKey) =>
  api.get(`/api/groups/invite/${encodeURIComponent(inviteKey)}/preview`);
export const joinGroupViaInvite = (inviteKey) =>
  api.post(`/api/groups/invite/${encodeURIComponent(inviteKey)}/join`);
export const fetchGroupInviteLink = (groupId) =>
  api.get(`/api/groups/${groupId}/invite-link`);
export const deferGroupInvite = (groupId) => api.post(`/api/groups/${groupId}/defer-invite`);
export const getGroup = (id) => api.get(`/api/groups/${id}`);
export const updateGroup = (groupId, data) => api.put(`/api/groups/${groupId}`, data);
export const uploadGroupCover = (groupId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/api/groups/${groupId}/upload-cover`, formData, {
    headers: { 'Content-Type': undefined },
  });
};
export const getGroupActivities = (groupId) => api.get(`/api/groups/${groupId}/activities`);
export const getGroupMembers = (groupId) => api.get(`/api/groups/${groupId}/members`);
export const createGroup = (data) => api.post('/api/groups/', data);
export const deleteGroup = (id) => api.delete(`/api/groups/${id}`);
export const exitGroup = (groupId) => api.post(`/api/groups/${groupId}/exit`);
export const addGroupMember = (groupId, searchTerm) =>
  api.post(`/api/groups/${groupId}/members`, { search_term: searchTerm });
export const searchGroupMembers = (groupId, phone) =>
  api.get(`/api/groups/${groupId}/members/search?phone=${encodeURIComponent(phone)}`);
export const removeGroupMember = (groupId, userId) =>
  api.delete(`/api/groups/${groupId}/members/${userId}`);
export const transferGroupOwnership = (groupId, newOwnerUserId) =>
  api.post(`/api/groups/${groupId}/transfer-ownership`, { new_owner_user_id: newOwnerUserId });

// Group Profile Fields
export const getGroupProfileFields = (groupId) =>
  api.get(`/api/groups/${groupId}/profile-fields`);
export const createGroupProfileField = (groupId, data) =>
  api.post(`/api/groups/${groupId}/profile-fields`, data);
export const updateGroupProfileField = (groupId, fieldId, data) =>
  api.put(`/api/groups/${groupId}/profile-fields/${fieldId}`, data);
export const deleteGroupProfileField = (groupId, fieldId) =>
  api.delete(`/api/groups/${groupId}/profile-fields/${fieldId}`);
export const getGroupOrganizers = (groupId) =>
  api.get(`/api/groups/${groupId}/organizers`);
export const joinGroupWithProfile = (groupId, profile = {}) =>
  api.post(`/api/groups/${groupId}/join-with-profile`, { profile });
export const getMemberProfile = (groupId, userId) =>
  api.get(`/api/groups/${groupId}/members/${userId}/profile`);
export const updateMemberProfile = (groupId, userId, values) =>
  api.put(`/api/groups/${groupId}/members/${userId}/profile`, { values });
export const getPendingProfileFields = (groupId) =>
  api.get(`/api/groups/${groupId}/pending-profile-fields`);

// Group Treasurer (Pro Payments)
export const getGroupTreasurer = (groupId) =>
  api.get(`/api/groups/${groupId}/treasurer`);
export const setupGroupTreasurer = (groupId, data) =>
  api.post(`/api/groups/${groupId}/treasurer`, data);
export const updateGroupTreasurer = (groupId, data) =>
  api.put(`/api/groups/${groupId}/treasurer`, data);

// Payments (data may include entered_amount for flexible pricing)
export const createPaymentOrder = (data) => api.post('/api/payments/create-order', data);
export const verifyPayment = (data) => api.post('/api/payments/verify', data);
export const getActivityPayments = (activityId) => api.get(`/api/payments/activity/${activityId}`);
export const getActivityRoster = (activityId) =>
  api.get(`/api/activities/${activityId}/roster`);
export const getMyActivitySmartPayment = (activityId) =>
  api.get(`/api/payments/activity/${activityId}/mine`);
export const getMyActivityBooking = (activityId) =>
  api.get(`/api/payments/activity/${activityId}/my-booking`);
export const downloadMyTicketPdf = (activityId) =>
  api.get(`/api/payments/activity/${activityId}/my-ticket.pdf`, {
    responseType: 'blob',
  });
export const getPayment = (paymentId) => api.get(`/api/payments/${paymentId}`);
export const smartConfirmPayment = (data) =>
  api.post('/api/payments/smart-confirm', data); // data may include entered_amount for flexible pricing
export const confirmPaymentReceipt = (paymentId, data = {}) =>
  api.post(`/api/payments/${paymentId}/confirm-receipt`, data);
export const rejectPayment = (paymentId, data = {}) =>
  api.post(`/api/payments/${paymentId}/reject`, data);
export const getMyPaymentHistory = () => api.get('/api/payments/my-history');

// Notifications
export const getNotifications = (params = {}) =>
  api.get('/api/notifications', { params: { skip: params.skip || 0, limit: params.limit || 50 } });
export const getUnreadNotificationCount = () => api.get('/api/notifications/unread-count');
export const markNotificationRead = (notificationId) =>
  api.post(`/api/notifications/${notificationId}/read`);
export const markAllNotificationsRead = () => api.post('/api/notifications/mark-all-read');

// Combined startup payload (activities + wallet + unread notifications)
export const getBootstrap = () => api.get('/api/bootstrap');

// Activity Payment Setup
export const updateActivityPaymentSetup = (activityId, data) =>
  api.patch(`/api/activities/${activityId}/payment-setup`, data);

export const updatePaymentCollection = (activityId, data) =>
  api.patch(`/api/activities/${activityId}/payment-collection`, data);

// Pro Payment Request (Treasurer Setup Reuse)
export const getMyProSetups = (groupId = null) =>
  api.get('/api/auth/me/pro-setups', { params: groupId ? { group_id: groupId } : {} });
export const getAvailableProSetups = (activityId) =>
  api.get(`/api/activities/${activityId}/available-pro-setups`);
export const applyOwnProSetup = (activityId, data) =>
  api.post(`/api/activities/${activityId}/apply-own-pro-setup`, data);
export const requestProSetup = (activityId, data) =>
  api.post(`/api/activities/${activityId}/request-pro-setup`, data);
export const getProRequestStatus = (activityId) =>
  api.get(`/api/activities/${activityId}/pro-request-status`);
export const getPendingProRequests = () =>
  api.get('/api/pro-payment-requests/pending');
export const getProRequest = (requestId) =>
  api.get(`/api/pro-payment-requests/${requestId}`);
export const respondToProRequest = (requestId, action) =>
  api.post(`/api/pro-payment-requests/${requestId}/respond`, { action });

// Payment screenshot (Share Target). totalAmount optional; used by direct-share flow.
export const uploadPaymentScreenshot = (activityId, file, totalAmount = null) => {
  const formData = new FormData();
  formData.append('activity_id', activityId);
  formData.append('file', file);
  if (totalAmount != null && Number(totalAmount) > 0) {
    formData.append('total_amount', totalAmount);
  }
  return api.post('/api/payments/upload-screenshot', formData, {
    transformRequest: [(data, headers) => {
      delete headers['Content-Type'];
      return data;
    }],
  });
};

// Expenses
export const createExpense = (data) => api.post('/api/expenses/', data);
export const getActivityExpenses = (activityId) => api.get(`/api/expenses/activity/${activityId}`);
export const updateExpense = (id, data) => api.put(`/api/expenses/${id}`, data);
export const deleteExpense = (id) => api.delete(`/api/expenses/${id}`);

// Schedule
export const getActivitySchedule = (activityId) =>
  api.get(`/api/activities/${activityId}/schedule`);
export const createScheduleItems = (activityId, items) =>
  api.post(`/api/activities/${activityId}/schedule`, { items });
export const updateScheduleItem = (itemId, data) =>
  api.put(`/api/schedule/${itemId}`, data);
export const deleteScheduleItem = (itemId) =>
  api.delete(`/api/schedule/${itemId}`);
export const extractSchedule = (activityId, { text, file } = {}) => {
  const formData = new FormData();
  if (text) formData.append('text', text);
  if (file) formData.append('file', file);
  return api.post(`/api/activities/${activityId}/schedule/extract`, formData, {
    headers: { 'Content-Type': undefined },
  });
};

// Expense categories (group-scoped)
export const getExpenseCategories = (activityId) =>
  api.get(`/api/expense-categories/activity/${activityId}`);
export const createExpenseCategory = (activityId, name) =>
  api.post(`/api/expense-categories/activity/${activityId}`, { name });

// Balance
export const getActivityBalance = (activityId) => api.get(`/api/balance/activity/${activityId}`);

// Wallet
export const getWalletBalance = () => api.get('/api/wallet/balance');
export const getGroupBalances = () => api.get('/api/wallet/group-balances');
export const getWalletTransactions = (skip = 0, limit = 50) => 
  api.get(`/api/wallet/transactions?skip=${skip}&limit=${limit}`);
export const concludeActivity = (activityId) => 
  api.post('/api/wallet/conclude-activity', { activity_id: activityId });

// Organizers
export const getActivityOrganizers = (activityId) => 
  api.get(`/api/activities/${activityId}/organizers`);
export const addOrganizer = (activityId, searchTerm) => 
  api.post(`/api/activities/${activityId}/organizers`, { 
    activity_id: activityId,
    search_term: searchTerm 
  });
export const removeOrganizer = (activityId, userId) =>
  api.delete(`/api/activities/${activityId}/organizers/${userId}`);

// Monthly dues & price rules
export const getMyDues = (activityId) => api.get(`/api/activities/${activityId}/dues/me`);
export const getDuesQuote = (activityId, body) =>
  api.post(`/api/activities/${activityId}/dues/quote`, body || {});
export const getDuesPeriods = (activityId) => api.get(`/api/activities/${activityId}/dues/periods`);
export const getDuesOverview = (activityId, period) =>
  api.get(`/api/activities/${activityId}/dues/overview`, { params: period ? { period } : {} });
export const getDuesMatrix = (activityId) => api.get(`/api/activities/${activityId}/dues/matrix`);
export const recordOfflinePayment = (activityId, body) =>
  api.post(`/api/activities/${activityId}/dues/offline-payments`, body);
export const previewOfflinePayment = (activityId, body) =>
  api.post(`/api/activities/${activityId}/dues/offline-preview`, body);
export const waiveDuesMonth = (activityId, body) =>
  api.post(`/api/activities/${activityId}/dues/waive`, body);
export const getPricingSetup = (activityId) => api.get(`/api/activities/${activityId}/pricing`);
export const savePricingSetup = (activityId, body) =>
  api.put(`/api/activities/${activityId}/pricing`, body);
export const changeBasePrice = (activityId, body) =>
  api.post(`/api/activities/${activityId}/price-change`, body);

// Excel Upload
export const getPaymentTemplate = (activityId) =>
  api.get('/api/activities/payment-template', {
    responseType: 'blob',
    params: activityId ? { activity_id: activityId } : {},
  });

export const uploadExcel = (activityId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/api/activities/${activityId}/upload-excel`, formData, {
    headers: { 'Content-Type': undefined },
  });
};

export const previewPaymentExcel = (activityId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/api/activities/${activityId}/upload-excel/preview`, formData, {
    headers: { 'Content-Type': undefined },
  });
};

export const getActivityReceivables = (activityId) =>
  api.get(`/api/activities/${activityId}/receivables`);
export const setActivityReceivables = (activityId, data) =>
  api.put(`/api/activities/${activityId}/receivables`, data);
export const deleteActivityReceivable = (activityId, userId) =>
  api.delete(`/api/activities/${activityId}/receivables/${userId}`);
export const recordReceivablePayment = (activityId, data) =>
  api.post(`/api/activities/${activityId}/receivables/offline-payment`, data);
export const getGroupCollections = (groupId) =>
  api.get(`/api/groups/${groupId}/collections`);
export const downloadGroupCollections = (groupId) =>
  api.get(`/api/groups/${groupId}/collections/export.xlsx`, { responseType: 'blob' });

// Expenses
export const uploadExpenseImage = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/api/expenses/upload-image', formData, {
    headers: { 'Content-Type': undefined },
  });
};

export const uploadExpenseExcel = (activityId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('activity_id', String(activityId));
  return api.post('/api/expenses/upload-excel', formData, {
    headers: { 'Content-Type': undefined },
  });
};

export const createExpensesBulk = (activityId, expenses) =>
  api.post('/api/expenses/bulk', { activity_id: activityId, expenses });

export const getExpenseTemplate = () =>
  api.get('/api/expenses/template', { responseType: 'blob' });

/** Organizer bills pack (PDF with photos). scope: month | last12 | all */
export const downloadExpenseBillsPdf = (activityId, { scope, period } = {}) =>
  api.get(`/api/expenses/activity/${activityId}/bills.pdf`, {
    params: {
      scope,
      ...(period ? { period } : {}),
    },
    responseType: 'blob',
  });

// Organizer Command Center
export const getOrganizerDashboard = (activityId) =>
  api.get(`/api/organizer/dashboard/${activityId}`);
export const ocrExpenseBill = (activityId, file) => {
  const formData = new FormData();
  formData.append('activity_id', activityId);
  formData.append('file', file);
  return api.post('/api/organizer/ocr/expense', formData, {
    headers: { 'Content-Type': undefined },
  });
};
export const ocrPaymentScreenshot = (file, activityId = null, paymentId = null) => {
  const formData = new FormData();
  formData.append('file', file);
  if (activityId != null) formData.append('activity_id', activityId);
  if (paymentId != null) formData.append('payment_id', paymentId);
  return api.post('/api/organizer/ocr/payment', formData, {
    headers: { 'Content-Type': undefined },
  });
};

// Budget Planning
export const getBudgetItems = (activityId) =>
  api.get(`/api/organizer/activity/${activityId}/budget`);
export const createBudgetItem = (activityId, data) =>
  api.post(`/api/organizer/activity/${activityId}/budget`, data);
export const updateBudgetItem = (itemId, data) =>
  api.put(`/api/organizer/budget/${itemId}`, data);
export const deleteBudgetItem = (itemId) =>
  api.delete(`/api/organizer/budget/${itemId}`);

// Offers
export const getOffers = (skip = 0, limit = 50) =>
  api.get(`/api/offers?skip=${skip}&limit=${limit}`);
export const getOffer = (offerId) => api.get(`/api/offers/${offerId}`);
export const recordOfferView = (offerId) => api.post(`/api/offers/${offerId}/view`);
export const claimOffer = (offerId) => api.post(`/api/offers/${offerId}/claim`);
export const notifyMeOffer = (offerId) => api.post(`/api/offers/${offerId}/notify-me`);

// Admin Offers
export const getAdminOffers = () => api.get('/api/admin/offers');
export const getAdminOfferClaims = (offerId) => api.get(`/api/admin/offers/${offerId}/claims`);
export const getAdminGenerateCoupon = () => api.get('/api/admin/offers/generate-coupon');
export const uploadAdminOfferImage = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/api/admin/offers/upload-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const createAdminOffer = (payload) => api.post('/api/admin/offers', payload);
export const updateAdminOffer = (offerId, payload) => api.patch(`/api/admin/offers/${offerId}`, payload);
export const deleteAdminOffer = (offerId) => api.delete(`/api/admin/offers/${offerId}`);

// Admin payment analytics (ADMIN_PHONES only)
export const getAdminPaymentStats = () => api.get('/api/admin/payments/stats');
export const getAdminPendingPayments = () => api.get('/api/admin/payments/pending');
export const postAdminPaymentSync = () => api.post('/api/admin/payments/sync');
export const getAdminRecentPaymentFailures = () => api.get('/api/admin/payments/recent-failures');
export const getAdminPaymentAuditTrail = (paymentId) =>
  api.get(`/api/admin/payments/${paymentId}/events`);

// Admin group commission settings
export const getAdminGroupsCommission = () => api.get('/api/admin/payments/groups-commission');
export const updateAdminGroupCommission = (groupId, data) =>
  api.put(`/api/admin/payments/groups/${groupId}/commission`, data);

// Admin merchant management
export const getAdminMerchants = () => api.get('/api/admin/payments/merchants');
export const addAdminMerchant = (userId) => api.post(`/api/admin/payments/merchants/${userId}`);
export const removeAdminMerchant = (userId) => api.delete(`/api/admin/payments/merchants/${userId}`);
export const searchAdminUsers = (query) => api.get(`/api/admin/payments/users/search?q=${encodeURIComponent(query)}`);
export const toggleMerchantRewards = (userId, enabled) =>
  api.put(`/api/admin/payments/merchants/${userId}/rewards`, { rewards_enabled: enabled });

/** Admin product metrics dashboard */
export const getAdminDashboardMetrics = () => api.get('/api/admin/metrics/dashboard');

// Admin feature releases / allowlist
export const getAdminFeatures = () => api.get('/api/admin/features');
export const updateAdminFeatureStatus = (key, data) =>
  api.patch(`/api/admin/features/${encodeURIComponent(key)}`, data);
export const addAdminFeatureAllowlistPhone = (data) =>
  api.post('/api/admin/features/allowlist', data);
export const removeAdminFeatureAllowlistPhone = (phone) =>
  api.delete(`/api/admin/features/allowlist/${encodeURIComponent(phone)}`);
export const getEnabledFeatures = () => api.get('/api/features/enabled');
/** First-party site visit ping (once per browser session per day) */
export const postVisitPing = () => api.post('/api/metrics/visit');

/** Plan / docs aliases (same endpoints as getAdmin* helpers above) */
export const getPaymentStats = getAdminPaymentStats;
export const getPendingPayments = getAdminPendingPayments;
export const syncPendingPayments = postAdminPaymentSync;
export const getRecentFailures = getAdminRecentPaymentFailures;
export const getPaymentAuditTrail = getAdminPaymentAuditTrail;

// Push Subscriptions
export const savePushSubscription = (subscription) =>
  api.post('/api/push-subscriptions', subscription);
export const deletePushSubscription = (endpoint) =>
  api.delete('/api/push-subscriptions', { params: { endpoint } });

// Event Chat
export const getChatMessages = (activityId, limit = 50, beforeId = null) => {
  let url = `/api/activities/${activityId}/chat/messages?limit=${limit}`;
  if (beforeId) url += `&before_id=${beforeId}`;
  return api.get(url);
};
export const sendChatMessage = (activityId, content) =>
  api.post(`/api/activities/${activityId}/chat/messages`, { content });
export const createToolFromRecommendation = (activityId, payload) =>
  api.post(`/api/activities/${activityId}/chat/create-tool`, payload);
export const seedEventChat = (activityId, intent) =>
  api.post(`/api/activities/${activityId}/chat/seed`, { intent });
export const markActivityChatRead = (activityId) =>
  api.post(`/api/activities/${activityId}/chat/mark-read`);
export const getEventTools = (activityId) =>
  api.get(`/api/activities/${activityId}/tools`);
export const submitPollVote = (activityId, toolId, body) =>
  api.post(`/api/activities/${activityId}/tools/${toolId}/vote`, body);
export const submitPlannerVote = (activityId, toolId, body) =>
  api.post(`/api/activities/${activityId}/tools/${toolId}/planner-vote`, body);
export const getActionItems = (activityId) =>
  api.get(`/api/activities/${activityId}/action-items`);

export const uploadActivityDescriptionImage = (activityId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/api/activities/${activityId}/description-image`, formData, {
    // Let the browser set multipart boundary. A bare multipart Content-Type
    // (plus this client's default application/json) makes FastAPI drop the file.
    headers: { 'Content-Type': undefined },
  });
};

export default api;

